// Image utilities for receipts (pre-OCR).
// Pipeline: capture → auto-detect receipt bounds → contrast/brightness → unsharp mask → output high-quality JPEG base64.

const DEFAULT_MAX_WIDTH = 2200;
const DEFAULT_QUALITY = 0.95;

// Storage compression — applied AFTER OCR so we keep high quality for the AI
// but store a leaner copy in the database (~85% smaller, still readable).
const STORAGE_MAX_WIDTH = 1400;
const STORAGE_QUALITY = 0.72;

/**
 * Compress a base64 / data URL for storage. Resizes to STORAGE_MAX_WIDTH
 * and re-encodes as JPEG at STORAGE_QUALITY. Returns a data URL.
 * Cuts a typical 4 MB receipt image down to ~600 KB while keeping the
 * receipt fully legible (text, QR code, prices).
 */
export async function compressForStorage(dataUrl, opts = {}) {
  if (!dataUrl) return dataUrl;
  const { maxWidth = STORAGE_MAX_WIDTH, quality = STORAGE_QUALITY } = opts;
  const img = await loadImage(dataUrl);
  const scale = Math.min(1, maxWidth / img.width);
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

/**
 * High-quality enhancement: contrast, brightness, light saturation, unsharp mask.
 */
export async function enhanceReceiptImage(file, opts = {}) {
  const { maxWidth = DEFAULT_MAX_WIDTH, quality = DEFAULT_QUALITY } = opts;
  const dataUrl = await fileToDataUrl(file);
  return enhanceDataUrl(dataUrl, { maxWidth, quality });
}

export async function enhanceDataUrl(dataUrl, opts = {}) {
  const { maxWidth = DEFAULT_MAX_WIDTH, quality = DEFAULT_QUALITY } = opts;
  const img = await loadImage(dataUrl);

  const scale = Math.min(1, maxWidth / img.width);
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  // Higher-quality resampling
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, w, h);

  const imageData = ctx.getImageData(0, 0, w, h);
  applyContrastBrightness(imageData, { contrast: 1.28, brightness: 6 });
  applyUnsharpMask(imageData, w, h, 0.6); // sharpen text edges
  ctx.putImageData(imageData, 0, 0);

  return canvas.toDataURL("image/jpeg", quality);
}

function clamp(v) {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

function applyContrastBrightness(imageData, { contrast, brightness }) {
  const data = imageData.data;
  const intercept = 128 * (1 - contrast);
  for (let i = 0; i < data.length; i += 4) {
    data[i] = clamp(data[i] * contrast + intercept + brightness);
    data[i + 1] = clamp(data[i + 1] * contrast + intercept + brightness);
    data[i + 2] = clamp(data[i + 2] * contrast + intercept + brightness);
  }
}

// Lightweight unsharp mask using a 3x3 sharpening kernel blended with the original.
function applyUnsharpMask(imageData, w, h, amount = 0.5) {
  const src = imageData.data;
  const out = new Uint8ClampedArray(src);
  // 3x3 sharpen kernel
  const k = [0, -1, 0, -1, 5, -1, 0, -1, 0];
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const idx = (y * w + x) * 4;
      for (let c = 0; c < 3; c++) {
        let acc = 0;
        let ki = 0;
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const ni = ((y + ky) * w + (x + kx)) * 4 + c;
            acc += src[ni] * k[ki++];
          }
        }
        const original = src[idx + c];
        out[idx + c] = clamp(original * (1 - amount) + acc * amount);
      }
    }
  }
  imageData.data.set(out);
}

// ---- Brightness-based segmentation (Otsu + largest connected component) ----
//
// This is the robust algorithm used by detectReceiptBoundsBox and removeBackground.
// Receipts are uniformly bright (white paper). Backgrounds are mostly darker or coloured.
// Otsu picks the optimal brightness threshold; the largest connected component of
// bright pixels is the receipt — even when the background contains busy textures with
// many strong edges (which broke the previous projection-based algorithm).
//
// Returns: { labels, largestLabel, dw, dh, scale, bounds: {minX,minY,maxX,maxY}, area } | null
async function segmentReceiptMask(dataUrl) {
  const img = await loadImage(dataUrl);
  const W = img.width;
  const H = img.height;
  if (W < 50 || H < 50) return null;

  // Downsample for speed — quality is preserved because the mask is upsampled later
  const targetW = Math.min(W, 600);
  const scale = targetW / W;
  const dw = Math.round(W * scale);
  const dh = Math.round(H * scale);

  const canvas = document.createElement("canvas");
  canvas.width = dw;
  canvas.height = dh;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, dw, dh);
  const data = ctx.getImageData(0, 0, dw, dh).data;

  // Grayscale + histogram
  const gray = new Uint8Array(dw * dh);
  const hist = new Uint32Array(256);
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    const v = (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) | 0;
    gray[p] = v;
    hist[v]++;
  }

  // Otsu's threshold
  const total = dw * dh;
  let sumAll = 0;
  for (let i = 0; i < 256; i++) sumAll += i * hist[i];
  let sumB = 0;
  let wB = 0;
  let maxVar = -1;
  let otsu = 127;
  for (let t = 0; t < 256; t++) {
    wB += hist[t];
    if (wB === 0) continue;
    const wF = total - wB;
    if (wF === 0) break;
    sumB += t * hist[t];
    const mB = sumB / wB;
    const mF = (sumAll - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    if (between > maxVar) {
      maxVar = between;
      otsu = t;
    }
  }
  // Bias toward bright threshold: receipts are very bright — but don't push the
  // threshold so high that shadowed/greyish parts of the receipt get cut off.
  const threshold = Math.max(otsu, 120);

  // Binary mask: 1 = bright/receipt candidate, 0 = background
  let mask = new Uint8Array(dw * dh);
  for (let i = 0; i < gray.length; i++) {
    mask[i] = gray[i] >= threshold ? 1 : 0;
  }
  // Morphological dilation (x2) bridges gaps caused by folds, shadows and dark
  // print bands so the receipt stays a single connected component.
  mask = dilateMask(mask, dw, dh);
  mask = dilateMask(mask, dw, dh);

  // 4-connected components via iterative DFS — keep ALL components so we can
  // merge receipt fragments that shadows/folds split apart.
  const labels = new Int32Array(dw * dh);
  let next = 0;
  const components = []; // {label, size, bounds}
  const stack = new Int32Array(dw * dh);
  for (let y = 0; y < dh; y++) {
    for (let x = 0; x < dw; x++) {
      const start = y * dw + x;
      if (mask[start] !== 1 || labels[start] !== 0) continue;
      next++;
      let top = 0;
      stack[top++] = start;
      let size = 0;
      let minX = x;
      let minY = y;
      let maxX = x;
      let maxY = y;
      while (top > 0) {
        const cur = stack[--top];
        if (labels[cur] !== 0) continue;
        labels[cur] = next;
        size++;
        const cy = (cur / dw) | 0;
        const cx = cur - cy * dw;
        if (cx < minX) minX = cx;
        if (cy < minY) minY = cy;
        if (cx > maxX) maxX = cx;
        if (cy > maxY) maxY = cy;
        if (cx > 0 && mask[cur - 1] === 1 && labels[cur - 1] === 0) stack[top++] = cur - 1;
        if (cx < dw - 1 && mask[cur + 1] === 1 && labels[cur + 1] === 0) stack[top++] = cur + 1;
        if (cy > 0 && mask[cur - dw] === 1 && labels[cur - dw] === 0) stack[top++] = cur - dw;
        if (cy < dh - 1 && mask[cur + dw] === 1 && labels[cur + dw] === 0) stack[top++] = cur + dw;
      }
      components.push({ label: next, size, bounds: { minX, minY, maxX, maxY } });
    }
  }

  if (components.length === 0) return null;
  components.sort((a, b) => b.size - a.size);
  const best = components[0];
  if (best.size / (dw * dh) < 0.03) return null;

  // Merge nearby significant fragments into the receipt bounds — a receipt cut
  // in two by a shadow shows up as 2 components very close to each other.
  const keepLabels = new Set([best.label]);
  const bestBounds = { ...best.bounds };
  const nearMargin = Math.round(Math.max(dw, dh) * 0.04);
  const minFragment = Math.max(best.size * 0.04, dw * dh * 0.004);
  for (let i = 1; i < components.length; i++) {
    const c = components[i];
    if (c.size < minFragment) continue;
    const b = c.bounds;
    const intersects =
      b.minX <= bestBounds.maxX + nearMargin && b.maxX >= bestBounds.minX - nearMargin &&
      b.minY <= bestBounds.maxY + nearMargin && b.maxY >= bestBounds.minY - nearMargin;
    if (intersects) {
      keepLabels.add(c.label);
      bestBounds.minX = Math.min(bestBounds.minX, b.minX);
      bestBounds.minY = Math.min(bestBounds.minY, b.minY);
      bestBounds.maxX = Math.max(bestBounds.maxX, b.maxX);
      bestBounds.maxY = Math.max(bestBounds.maxY, b.maxY);
    }
  }

  return { labels, largestLabel: best.label, keepLabels, dw, dh, scale, bounds: bestBounds, area: best.size, W, H };
}

// 3x3 dilation of a binary mask.
function dilateMask(mask, w, h) {
  const out = new Uint8Array(mask.length);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (mask[i] === 1) { out[i] = 1; continue; }
      let hit = 0;
      for (let ky = -1; ky <= 1 && !hit; ky++) {
        const ny = y + ky;
        if (ny < 0 || ny >= h) continue;
        for (let kx = -1; kx <= 1; kx++) {
          const nx = x + kx;
          if (nx < 0 || nx >= w) continue;
          if (mask[ny * w + nx] === 1) { hit = 1; break; }
        }
      }
      out[i] = hit;
    }
  }
  return out;
}

/**
 * Same as autoDetectAndCrop but returns the detected bounding box in ORIGINAL image
 * coordinates (and not the cropped image). Returns null if no meaningful box is detected.
 *
 * Algorithm: Otsu threshold + largest 4-connected component (robust to busy backgrounds).
 *
 * Returns: { x, y, width, height, imageWidth, imageHeight } | null
 */
export async function detectReceiptBoundsBox(dataUrl, opts = {}) {
  // Generous padding (4.5%) so the receipt edges are never clipped.
  const { padPercent = 0.045, minAreaShrink = 0.97 } = opts;
  const seg = await segmentReceiptMask(dataUrl);
  if (!seg) return null;

  const { bounds, scale, W, H } = seg;
  const w = bounds.maxX - bounds.minX;
  const h = bounds.maxY - bounds.minY;
  if (w < 20 || h < 20) return null;

  const padX = Math.round(w * padPercent);
  const padY = Math.round(h * padPercent);
  const cropL = Math.max(0, Math.round((bounds.minX - padX) / scale));
  const cropT = Math.max(0, Math.round((bounds.minY - padY) / scale));
  const cropR = Math.min(W - 1, Math.round((bounds.maxX + padX) / scale));
  const cropB = Math.min(H - 1, Math.round((bounds.maxY + padY) / scale));
  const cropW = cropR - cropL;
  const cropH = cropB - cropT;
  if (cropW <= 0 || cropH <= 0) return null;
  if ((cropW * cropH) / (W * H) > minAreaShrink) return null;

  return { x: cropL, y: cropT, width: cropW, height: cropH, imageWidth: W, imageHeight: H };
}

/**
 * Crops a data URL using the given bounds (in original image coords) and returns a new JPEG data URL.
 */
export async function cropImageWithBounds(dataUrl, bounds) {
  const img = await loadImage(dataUrl);
  return cropAndExport(img, bounds.x, bounds.y, bounds.width, bounds.height);
}

/**
 * Background removal — replaces pixels OUTSIDE the detected receipt mask with white.
 *
 * Uses the brightness-based segmentation (Otsu + largest connected component): the receipt
 * mask is computed at downsampled resolution, then nearest-neighbour upsampled and applied
 * to the full-resolution image. Pixels not belonging to the receipt component become white.
 *
 * Works on real photos where the receipt sits on a complex background (carpets, fabrics,
 * objects). Falls back to the colour-distance heuristic only when segmentation fails.
 *
 * @param {string} dataUrl - input image data URL
 * @param {object} [opts]
 * @param {number} [opts.aggressiveness=1.0] - reserved for the fallback path
 * @returns {Promise<string>} JPEG data URL
 */
export async function removeBackground(dataUrl, opts = {}) {
  const { aggressiveness = 1.0, quality = 0.95 } = opts;

  const seg = await segmentReceiptMask(dataUrl);

  // Fallback: colour-distance heuristic if segmentation fails
  if (!seg) return removeBackgroundByColorDistance(dataUrl, aggressiveness, quality);

  const { labels, largestLabel, keepLabels, dw, dh, scale } = seg;
  const keep = keepLabels || new Set([largestLabel]);
  const img = await loadImage(dataUrl);
  const W = img.width;
  const H = img.height;

  const out = document.createElement("canvas");
  out.width = W;
  out.height = H;
  const octx = out.getContext("2d", { willReadFrequently: true });
  octx.drawImage(img, 0, 0);
  const outImage = octx.getImageData(0, 0, W, H);
  const outData = outImage.data;

  // Nearest-neighbour resample of the mask back to original size
  for (let y = 0; y < H; y++) {
    const dy = Math.min(dh - 1, (y * scale) | 0);
    const rowOff = dy * dw;
    for (let x = 0; x < W; x++) {
      const dx = Math.min(dw - 1, (x * scale) | 0);
      if (!keep.has(labels[rowOff + dx])) {
        const i = (y * W + x) * 4;
        outData[i] = 255;
        outData[i + 1] = 255;
        outData[i + 2] = 255;
      }
    }
  }
  octx.putImageData(outImage, 0, 0);
  return out.toDataURL("image/jpeg", quality);
}

// Legacy colour-distance fallback (used only when segmentation fails to find a receipt).
async function removeBackgroundByColorDistance(dataUrl, aggressiveness, quality) {
  const img = await loadImage(dataUrl);
  const W = img.width;
  const H = img.height;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, W, H);
  const data = imageData.data;
  const cornerSize = Math.max(6, Math.floor(Math.min(W, H) * 0.025));
  const corners = [
    [0, 0],
    [W - cornerSize, 0],
    [0, H - cornerSize],
    [W - cornerSize, H - cornerSize],
  ];
  let rSum = 0;
  let gSum = 0;
  let bSum = 0;
  let n = 0;
  for (const [cx, cy] of corners) {
    for (let dy = 0; dy < cornerSize; dy++) {
      for (let dx = 0; dx < cornerSize; dx++) {
        const i = ((cy + dy) * W + (cx + dx)) * 4;
        rSum += data[i];
        gSum += data[i + 1];
        bSum += data[i + 2];
        n++;
      }
    }
  }
  const bgR = rSum / n;
  const bgG = gSum / n;
  const bgB = bSum / n;
  if ((bgR + bgG + bgB) / 3 > 220) return dataUrl;
  const threshold2 = (55 * aggressiveness) ** 2;
  for (let i = 0; i < data.length; i += 4) {
    const dr = data[i] - bgR;
    const dg = data[i + 1] - bgG;
    const db = data[i + 2] - bgB;
    if (dr * dr + dg * dg + db * db < threshold2) {
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
    }
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL("image/jpeg", quality);
}

/**
 * Auto-detect the receipt content area within the image and return a cropped data URL.
 * Returns null if no meaningful crop is detected (e.g. receipt fills the frame).
 *
 * Algorithm (edge-projection — Hough-lite):
 *   1. Convert to grayscale into a typed array.
 *   2. Apply Sobel-like gradients to get edge magnitude per pixel.
 *   3. Project edge energy onto X and Y axes (column sums + row sums).
 *   4. Smooth each profile with a small box-blur to remove noise.
 *   5. Find first/last row & col where smoothed energy exceeds a robust threshold
 *      (mean + k·std of the profile). These four lines are the receipt borders.
 *   6. Compute bounding box + small padding, then crop.
 *
 *  Falls back to the corner-color heuristic if the edge approach fails or finds nothing useful.
 */
export async function autoDetectAndCrop(dataUrl, opts = {}) {
  const bounds = await detectReceiptBoundsBox(dataUrl, opts);
  if (bounds) {
    return cropImageWithBounds(dataUrl, bounds);
  }
  return null;
}

// ---- helpers for the edge algorithm ----

function smooth(arr, radius) {
  const n = arr.length;
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    let s = 0;
    let c = 0;
    for (let k = -radius; k <= radius; k++) {
      const j = i + k;
      if (j >= 0 && j < n) {
        s += arr[j];
        c++;
      }
    }
    out[i] = s / c;
  }
  arr.set(out);
}

// Sweep through profile and return the first index whose value > mean + k·std.
function findFirstAbove(profile, start, end, step) {
  const n = profile.length;
  // Compute mean + std of interior (skip 2% margin)
  const m = Math.floor(n * 0.02);
  let sum = 0;
  let count = 0;
  for (let i = m; i < n - m; i++) {
    sum += profile[i];
    count++;
  }
  const mean = sum / count;
  let variance = 0;
  for (let i = m; i < n - m; i++) {
    variance += (profile[i] - mean) ** 2;
  }
  variance /= count;
  const std = Math.sqrt(variance);
  const threshold = mean + 0.4 * std;

  for (let i = start; step > 0 ? i <= end : i >= end; i += step) {
    if (profile[i] > threshold) return i;
  }
  return null;
}

async function cornerHeuristicCrop(dataUrl, img, data, W, H, padPercent, minAreaShrink) {
  // Legacy corner-based fallback (faster but less accurate)
  const cornerSamples = [
    [2, 2], [W - 3, 2], [2, H - 3], [W - 3, H - 3],
  ].map(([x, y]) => {
    const i = (y * W + x) * 4;
    return (data[i] + data[i + 1] + data[i + 2]) / 3;
  });
  const bg = cornerSamples.reduce((s, v) => s + v, 0) / cornerSamples.length;

  const step = Math.max(1, Math.floor(Math.min(W, H) / 250));
  let minX = W; let minY = H; let maxX = 0; let maxY = 0;
  let contentFound = false;
  for (let y = 0; y < H; y += step) {
    for (let x = 0; x < W; x += step) {
      const i = (y * W + x) * 4;
      const b = (data[i] + data[i + 1] + data[i + 2]) / 3;
      if (Math.abs(b - bg) > 30) {
        contentFound = true;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (!contentFound || maxX <= minX || maxY <= minY) return null;
  const padX = Math.round((maxX - minX) * padPercent);
  const padY = Math.round((maxY - minY) * padPercent);
  minX = Math.max(0, minX - padX);
  minY = Math.max(0, minY - padY);
  maxX = Math.min(W - 1, maxX + padX);
  maxY = Math.min(H - 1, maxY + padY);
  const cropW = maxX - minX;
  const cropH = maxY - minY;
  if ((cropW * cropH) / (W * H) > minAreaShrink) return null;
  return cropAndExport(img, minX, minY, cropW, cropH);
}

function cropAndExport(img, x, y, w, h) {
  const out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  const octx = out.getContext("2d");
  octx.imageSmoothingEnabled = true;
  octx.imageSmoothingQuality = "high";
  octx.drawImage(img, x, y, w, h, 0, 0, w, h);
  return out.toDataURL("image/jpeg", 0.95);
}

export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Render a PDF invoice into a single JPEG data URL (pages stitched vertically,
 * up to maxPages). Uses pdfjs-dist with the worker served from /public.
 */
export async function pdfToDataUrl(file, opts = {}) {
  const { maxPages = 3, targetWidth = 1600 } = opts;
  const pdfjs = await import("pdfjs-dist");
  pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
  const buf = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: buf }).promise;
  const n = Math.min(pdf.numPages, maxPages);
  const pages = [];
  let totalH = 0;
  let maxW = 0;
  for (let i = 1; i <= n; i++) {
    const page = await pdf.getPage(i);
    const vp1 = page.getViewport({ scale: 1 });
    const scale = Math.min(3, targetWidth / vp1.width);
    const vp = page.getViewport({ scale });
    const c = document.createElement("canvas");
    c.width = Math.round(vp.width);
    c.height = Math.round(vp.height);
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, c.width, c.height);
    await page.render({ canvasContext: ctx, viewport: vp }).promise;
    pages.push(c);
    totalH += c.height;
    maxW = Math.max(maxW, c.width);
  }
  if (pages.length === 1) return pages[0].toDataURL("image/jpeg", 0.92);
  const out = document.createElement("canvas");
  out.width = maxW;
  out.height = totalH;
  const octx = out.getContext("2d");
  octx.fillStyle = "#FFFFFF";
  octx.fillRect(0, 0, maxW, totalH);
  let y = 0;
  for (const c of pages) {
    octx.drawImage(c, Math.round((maxW - c.width) / 2), y);
    y += c.height;
  }
  return out.toDataURL("image/jpeg", 0.92);
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// ---------- Display helpers ----------

export function formatCurrency(amount, currency = "SAR") {
  const symbols = {
    SAR: "ر.س", AED: "د.إ", EGP: "ج.م", USD: "$", EUR: "€",
    KWD: "د.ك", QAR: "ر.ق", BHD: "د.ب", OMR: "ر.ع", JOD: "د.أ",
  };
  const sym = symbols[currency] || currency;
  // Western digits with comma grouping for clear financial reading: 1,234.56
  const n = Number(amount || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${n} ${sym}`;
}

export function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    // Arabic month/day names + Western digits (e.g., "٩ يونيو ٢٠٢٦" → "9 يونيو 2026")
    return new Date(dateStr).toLocaleDateString("ar-SA-u-nu-latn", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}
